import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import { Check, ChevronsUpDown, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatCurrencyWithDecimals = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const LabelWithTooltip = ({ text, tooltip }) => (
  <div className="flex items-center gap-2">
    <Label className="text-[#5C4033]">{text}</Label>
    {tooltip ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-[#A8A8A8] hover:text-[#5C4033] transition-colors"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    ) : null}
  </div>
);

export default function PricingCostingPage() {
  const { variantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [showApplyAllDialog, setShowApplyAllDialog] = useState(false);
  const [variants, setVariants] = useState([]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [variant, setVariant] = useState(null);
  const [pricingData, setPricingData] = useState({
    costing: null,
    pricing: null,
    rules: null,
    discounts: [],
    history: []
  });

  const [costingForm, setCostingForm] = useState({
    raw_material_cost_per_kg: '',
    processing_cost_per_kg: '',
    packaging_cost_per_kg: '',
    overhead_cost_per_kg: '',
    wastage_percentage: '',
    last_reviewed_on: '',
    reason: ''
  });
  const [pricingForm, setPricingForm] = useState({
    retail_mrp: '',
    retail_price: '',
    wholesale_price: '',
    pricing_basis: 'COST_BASED',
    active: true,
    reason: ''
  });
  const [rulesForm, setRulesForm] = useState({
    minimum_margin_percentage: '',
    max_discount_end_customer: '',
    max_discount_end_customer_type: 'FLAT',
    max_discount_retail_shop: '',
    max_discount_retail_shop_type: 'FLAT',
    max_discount_wholesale: '',
    max_discount_wholesale_type: 'FLAT',
    warning_threshold_percentage: '0',
    hard_block: true,
    allow_below_minimum: false,
    no_discount: false,
    reason: ''
  });
  const [discountSlabs, setDiscountSlabs] = useState([]);
  const [discountReason, setDiscountReason] = useState('');

  const buyerTypes = [
    { value: 'END_CUSTOMER', label: 'End Customer' },
    { value: 'RETAIL_SHOP', label: 'Retail Shop' },
    { value: 'WHOLESALE', label: 'Wholesale Customer' }
  ];

  const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatDateInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const loadPricingModule = async () => {
    if (!variantId) {
      setVariant(null);
      setPricingData({ costing: null, pricing: null, rules: null, discounts: [], history: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await adminApi.getVariantPricingModule(variantId);
      const data = response.data || {};
      setVariant(data.variant || null);
      setPricingData({
        costing: data.costing || null,
        pricing: data.pricing || null,
        rules: data.rules || null,
        discounts: data.discounts || [],
        history: data.history || []
      });

      const costing = data.costing || {};
      setCostingForm({
        raw_material_cost_per_kg: costing.raw_material_cost_per_kg?.toString() || '',
        processing_cost_per_kg: costing.processing_cost_per_kg?.toString() || '',
        packaging_cost_per_kg: costing.packaging_cost_per_kg?.toString() || '',
        overhead_cost_per_kg: costing.overhead_cost_per_kg?.toString() || '',
        wastage_percentage: costing.wastage_percentage?.toString() || '',
        last_reviewed_on: formatDateInput(costing.last_reviewed_on),
        reason: ''
      });

      const pricing = data.pricing || {};
      setPricingForm({
        retail_mrp: pricing.retail_mrp?.toString() || '',
        retail_price: pricing.retail_price?.toString() || '',
        wholesale_price: pricing.wholesale_price?.toString() || '',
        pricing_basis: pricing.pricing_basis || 'COST_BASED',
        active: pricing.active ?? true,
        reason: ''
      });

      const rules = data.rules || {};
      const noDiscountDetected =
        parseNumber(rules.max_discount_end_customer) === 0 &&
        parseNumber(rules.max_discount_retail_shop) === 0 &&
        parseNumber(rules.max_discount_wholesale) === 0 &&
        (rules.max_discount_end_customer_type || 'PERCENTAGE') === 'FLAT' &&
        (rules.max_discount_retail_shop_type || 'PERCENTAGE') === 'FLAT' &&
        (rules.max_discount_wholesale_type || 'PERCENTAGE') === 'FLAT';
      setRulesForm({
        minimum_margin_percentage: rules.minimum_margin_percentage?.toString() || '',
        max_discount_end_customer: rules.max_discount_end_customer?.toString() || '',
        max_discount_end_customer_type: rules.max_discount_end_customer_type || 'FLAT',
        max_discount_retail_shop: rules.max_discount_retail_shop?.toString() || '',
        max_discount_retail_shop_type: rules.max_discount_retail_shop_type || 'FLAT',
        max_discount_wholesale: rules.max_discount_wholesale?.toString() || '',
        max_discount_wholesale_type: rules.max_discount_wholesale_type || 'FLAT',
        warning_threshold_percentage: rules.warning_threshold_percentage?.toString() || '0',
        hard_block: rules.hard_block ?? true,
        allow_below_minimum: rules.allow_below_minimum ?? false,
        no_discount: noDiscountDetected,
        reason: ''
      });

      setDiscountSlabs(
        (data.discounts || []).map((slab) => ({
          id: slab.id,
          buyer_type: slab.buyer_type || 'RETAIL',
          min_quantity_kg: slab.min_quantity_kg?.toString() || '',
          max_quantity_kg: slab.max_quantity_kg?.toString() || '',
          discount_type: slab.discount_type || 'PERCENTAGE',
          discount_value: slab.discount_value?.toString() || '',
          active: slab.active ?? true
        }))
      );
    } catch (error) {
      toast.error('Failed to load pricing module');
    } finally {
      setLoading(false);
    }
  };

  const loadVariants = async () => {
    try {
      const response = await adminApi.getProductVariants({ is_active: true });
      setVariants(response.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  useEffect(() => {
    loadVariants();
    loadPricingModule();
  }, [variantId]);

  const computeCostingTotals = () => {
    const packGrams = variant?.pack_size_grams || 0;
    const subtotal =
      parseNumber(costingForm.raw_material_cost_per_kg) +
      parseNumber(costingForm.processing_cost_per_kg) +
      parseNumber(costingForm.packaging_cost_per_kg) +
      parseNumber(costingForm.overhead_cost_per_kg);
    const wastageMultiplier = 1 + parseNumber(costingForm.wastage_percentage) / 100;
    const totalCostPerKg = subtotal * wastageMultiplier;
    const totalCostPerPack = totalCostPerKg * (packGrams / 1000);
    return {
      totalCostPerKg: Number.isFinite(totalCostPerKg) ? totalCostPerKg : 0,
      totalCostPerPack: Number.isFinite(totalCostPerPack) ? totalCostPerPack : 0
    };
  };

  const computeMinimumSellingPrice = () => {
    const totals = computeCostingTotals();
    const minMargin = parseNumber(rulesForm.minimum_margin_percentage);
    const minPricePerKg = totals.totalCostPerKg * (1 + minMargin / 100);
    const minPricePerPack = totals.totalCostPerPack * (1 + minMargin / 100);
    return {
      minPricePerKg: Number.isFinite(minPricePerKg) ? minPricePerKg : 0,
      minPricePerPack: Number.isFinite(minPricePerPack) ? minPricePerPack : 0
    };
  };

  const getPricingErrors = () => {
    const errors = [];
    const totals = computeCostingTotals();
    if (!totals.totalCostPerPack || !parseNumber(rulesForm.minimum_margin_percentage)) {
      return errors;
    }
    const roundToTwo = (value) => Math.round((value || 0) * 100) / 100;
    const minMargin = parseNumber(rulesForm.minimum_margin_percentage);
    const minPricePerPack = roundToTwo(totals.totalCostPerPack * (1 + minMargin / 100));
    const entries = [
      { label: 'End Customer (MRP)', value: parseNumber(pricingForm.retail_mrp) },
      { label: 'Retail Shop', value: parseNumber(pricingForm.retail_price) },
      { label: 'Wholesale', value: parseNumber(pricingForm.wholesale_price) }
    ];
    entries.forEach((entry) => {
      const priceRounded = roundToTwo(entry.value);
      if (priceRounded && priceRounded < minPricePerPack) {
        errors.push(`${entry.label} price below minimum selling price.`);
      }
      if (entry.value && totals.totalCostPerPack) {
        const margin = ((entry.value - totals.totalCostPerPack) / totals.totalCostPerPack) * 100;
        const marginRounded = roundToTwo(margin);
        if (marginRounded < minMargin) {
          errors.push(`${entry.label} margin below minimum.`);
        }
      }
    });
    return errors;
  };

  const costingTotals = useMemo(computeCostingTotals, [costingForm, variant]);
  const minSelling = useMemo(computeMinimumSellingPrice, [costingForm, rulesForm, variant]);
  const pricingErrors = getPricingErrors();
  const pricingReady = Boolean(pricingData.costing && pricingData.rules);
  const rulesReady = Boolean(pricingData.costing);
  const discountsReady = Boolean(pricingData.pricing && pricingData.rules);
  const sortedVariants = useMemo(() => {
    return [...variants].sort((a, b) => {
      const nameA = `${a.master_name || ''} ${a.pack_size_label || ''}`.trim().toLowerCase();
      const nameB = `${b.master_name || ''} ${b.pack_size_label || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [variants]);
  const mspPerPack = minSelling.minPricePerPack || 0;

  const maxDiscountEffective = (basePrice, maxDiscount) => {
    const base = parseNumber(basePrice);
    const discount = parseNumber(maxDiscount);
    if (!base) return 0;
    return base * (1 - discount / 100);
  };
  const maxDiscountEffectiveByType = (basePrice, maxDiscount, discountType) => {
    const base = parseNumber(basePrice);
    const discount = parseNumber(maxDiscount);
    if (!base) return 0;
    if (discountType === 'FLAT') return base - discount;
    return base * (1 - discount / 100);
  };
  const priceStatus = (effectivePrice) => {
    if (!mspPerPack) return { color: 'text-[#A8A8A8]', warn: false };
    return effectivePrice < mspPerPack
      ? { color: 'text-red-600', warn: true }
      : { color: 'text-green-600', warn: false };
  };

  const handleSaveCosting = async () => {
    if (!variantId) return;
    if (!costingForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const payload = {
        raw_material_cost_per_kg: parseNumber(costingForm.raw_material_cost_per_kg),
        processing_cost_per_kg: parseNumber(costingForm.processing_cost_per_kg),
        packaging_cost_per_kg: parseNumber(costingForm.packaging_cost_per_kg),
        overhead_cost_per_kg: parseNumber(costingForm.overhead_cost_per_kg),
        wastage_percentage: parseNumber(costingForm.wastage_percentage),
        last_reviewed_on: today,
        reason: costingForm.reason
      };
      await adminApi.updateVariantCosting(variantId, payload);
      toast.success('Costing updated');
      setCostingForm((prev) => ({ ...prev, reason: '' }));
      await loadPricingModule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update costing');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePricing = async () => {
    if (!variantId) return;
    if (!pricingForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (pricingErrors.length > 0) {
      toast.error(pricingErrors[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        retail_mrp: parseNumber(pricingForm.retail_mrp),
        retail_price: parseNumber(pricingForm.retail_price),
        wholesale_price: parseNumber(pricingForm.wholesale_price),
        pricing_basis: pricingForm.pricing_basis,
        active: pricingForm.active,
        reason: pricingForm.reason
      };
      await adminApi.updateVariantPricing(variantId, payload);
      toast.success('Pricing updated');
      setPricingForm((prev) => ({ ...prev, reason: '' }));
      await loadPricingModule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRules = async () => {
    if (!variantId) return;
    if (!rulesForm.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setSaving(true);
    try {
      const noDiscount = rulesForm.no_discount;
      const payload = {
        minimum_margin_percentage: parseNumber(rulesForm.minimum_margin_percentage),
        max_discount_end_customer: noDiscount ? 0 : parseNumber(rulesForm.max_discount_end_customer),
        max_discount_end_customer_type: noDiscount ? 'FLAT' : (rulesForm.max_discount_end_customer_type || 'FLAT'),
        max_discount_retail_shop: noDiscount ? 0 : parseNumber(rulesForm.max_discount_retail_shop),
        max_discount_retail_shop_type: noDiscount ? 'FLAT' : (rulesForm.max_discount_retail_shop_type || 'FLAT'),
        max_discount_wholesale: noDiscount ? 0 : parseNumber(rulesForm.max_discount_wholesale),
        max_discount_wholesale_type: noDiscount ? 'FLAT' : (rulesForm.max_discount_wholesale_type || 'FLAT'),
        warning_threshold_percentage: parseNumber(rulesForm.warning_threshold_percentage || '0'),
        hard_block: rulesForm.hard_block,
        allow_below_minimum: rulesForm.allow_below_minimum,
        reason: rulesForm.reason
      };
      await adminApi.updateVariantPriceRules(variantId, payload);
      toast.success('Pricing rules updated');
      setRulesForm((prev) => ({ ...prev, reason: '' }));
      await loadPricingModule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rules');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDiscounts = async () => {
    if (!variantId) return;
    if (rulesForm.no_discount) {
      toast.error('Disable "No Discount" to edit discount slabs');
      return;
    }
    if (!discountReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slabs: discountSlabs.map((slab) => ({
          buyer_type: slab.buyer_type,
          min_quantity_kg: parseNumber(slab.min_quantity_kg),
          max_quantity_kg: parseNumber(slab.max_quantity_kg),
          discount_type: slab.discount_type,
          discount_value: parseNumber(slab.discount_value),
          active: slab.active
        })),
        reason: discountReason
      };
      await adminApi.updateVariantDiscountSlabs(variantId, payload);
      toast.success('Discount slabs updated');
      setDiscountReason('');
      await loadPricingModule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update discounts');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDiscountSlab = () => {
    setDiscountSlabs((prev) => ([
      ...prev,
      {
        buyer_type: 'RETAIL',
        min_quantity_kg: '',
        max_quantity_kg: '',
        discount_type: 'FLAT',
        discount_value: '',
        active: true
      }
    ]));
  };

  const handleRemoveDiscountSlab = (index) => {
    setDiscountSlabs((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleToggleNoDiscount = (checked) => {
    setRulesForm((prev) => ({
      ...prev,
      no_discount: checked,
      max_discount_end_customer: checked ? '0' : prev.max_discount_end_customer,
      max_discount_retail_shop: checked ? '0' : prev.max_discount_retail_shop,
      max_discount_wholesale: checked ? '0' : prev.max_discount_wholesale,
      max_discount_end_customer_type: 'FLAT',
      max_discount_retail_shop_type: 'FLAT',
      max_discount_wholesale_type: 'FLAT'
    }));
    if (checked) {
      setDiscountSlabs([]);
    } else {
      setDiscountSlabs((prev) => prev.map((slab) => ({
        ...slab,
        discount_type: slab.discount_type || 'FLAT'
      })));
    }
  };

  const scaleByRatio = (value, ratio) => {
    const numericValue = parseNumber(value);
    const numericRatio = Number(ratio);
    if (!Number.isFinite(numericValue) || !Number.isFinite(numericRatio)) return 0;
    return Number((numericValue * numericRatio).toFixed(4));
  };

  const handleApplyToAllVariants = async () => {
    if (!variantId || !variant) return;
    if (!pricingReady) {
      toast.error('Set and save costing, rules, and pricing before applying to all variants');
      return;
    }

    const currentPackGrams = Number(variant.pack_size_grams || 0);
    if (!currentPackGrams) {
      toast.error('Current variant pack size is invalid');
      return;
    }

    const siblingVariants = variants.filter(
      (item) => item.id !== variantId && item.master_id === variant.master_id
    );

    if (!siblingVariants.length) {
      toast.info('No other variants found for this product');
      return;
    }

    const currentPackagingPerPack = parseNumber(costingForm.packaging_cost_per_kg) * (currentPackGrams / 1000);
    const baseReason = `Apply to all variants from ${variant.master_name} ${variant.pack_size_label}`;

    setApplyingToAll(true);
    const failed = [];

    for (const target of siblingVariants) {
      try {
        const targetPackGrams = Number(target.pack_size_grams || 0);
        if (!targetPackGrams) {
          throw new Error('Invalid target pack size');
        }

        const packRatio = targetPackGrams / currentPackGrams;
        const targetPackagingPerKg = currentPackagingPerPack * (1000 / targetPackGrams);

        await adminApi.updateVariantCosting(target.id, {
          raw_material_cost_per_kg: parseNumber(costingForm.raw_material_cost_per_kg),
          processing_cost_per_kg: parseNumber(costingForm.processing_cost_per_kg),
          packaging_cost_per_kg: Number(targetPackagingPerKg.toFixed(4)),
          overhead_cost_per_kg: parseNumber(costingForm.overhead_cost_per_kg),
          wastage_percentage: parseNumber(costingForm.wastage_percentage),
          last_reviewed_on: new Date().toISOString().split('T')[0],
          reason: baseReason
        });

        await adminApi.updateVariantPriceRules(target.id, {
          minimum_margin_percentage: parseNumber(rulesForm.minimum_margin_percentage),
          max_discount_end_customer: rulesForm.max_discount_end_customer_type === 'FLAT'
            ? scaleByRatio(rulesForm.max_discount_end_customer, packRatio)
            : parseNumber(rulesForm.max_discount_end_customer),
          max_discount_end_customer_type: rulesForm.max_discount_end_customer_type || 'FLAT',
          max_discount_retail_shop: rulesForm.max_discount_retail_shop_type === 'FLAT'
            ? scaleByRatio(rulesForm.max_discount_retail_shop, packRatio)
            : parseNumber(rulesForm.max_discount_retail_shop),
          max_discount_retail_shop_type: rulesForm.max_discount_retail_shop_type || 'FLAT',
          max_discount_wholesale: rulesForm.max_discount_wholesale_type === 'FLAT'
            ? scaleByRatio(rulesForm.max_discount_wholesale, packRatio)
            : parseNumber(rulesForm.max_discount_wholesale),
          max_discount_wholesale_type: rulesForm.max_discount_wholesale_type || 'FLAT',
          warning_threshold_percentage: parseNumber(rulesForm.warning_threshold_percentage || '0'),
          hard_block: rulesForm.hard_block,
          allow_below_minimum: rulesForm.allow_below_minimum,
          reason: baseReason
        });

        await adminApi.updateVariantPricing(target.id, {
          retail_mrp: scaleByRatio(pricingForm.retail_mrp, packRatio),
          retail_price: scaleByRatio(pricingForm.retail_price, packRatio),
          wholesale_price: scaleByRatio(pricingForm.wholesale_price, packRatio),
          pricing_basis: pricingForm.pricing_basis,
          active: pricingForm.active,
          reason: baseReason
        });

        if (!rulesForm.no_discount) {
          await adminApi.updateVariantDiscountSlabs(target.id, {
            slabs: discountSlabs.map((slab) => ({
              buyer_type: slab.buyer_type,
              min_quantity_kg: parseNumber(slab.min_quantity_kg),
              max_quantity_kg: parseNumber(slab.max_quantity_kg),
              discount_type: slab.discount_type,
              discount_value: slab.discount_type === 'FLAT'
                ? scaleByRatio(slab.discount_value, packRatio)
                : parseNumber(slab.discount_value),
              active: slab.active
            })),
            reason: baseReason
          });
        }
      } catch (error) {
        failed.push({ name: `${target.master_name} ${target.pack_size_label}`, error });
      }
    }

    setApplyingToAll(false);

    if (failed.length) {
      toast.error(`Applied with ${failed.length} failure(s). Please retry.`);
    } else {
      toast.success(`Applied pricing settings to ${siblingVariants.length} variant(s)`);
    }

    await loadPricingModule();
  };

  const handleRequestApplyToAllVariants = () => {
    if (!variantId || !variant) return;
    if (!pricingReady) {
      toast.error('Set and save costing, rules, and pricing before applying to all variants');
      return;
    }
    if (pricingErrors.length > 0) {
      toast.error(pricingErrors[0]);
      return;
    }
    const siblingVariants = variants.filter(
      (item) => item.id !== variantId && item.master_id === variant.master_id
    );
    if (!siblingVariants.length) {
      toast.info('No other variants found for this product');
      return;
    }
    setShowApplyAllDialog(true);
  };

  return (
    <TooltipProvider>
      <div className="space-y-6 relative" data-testid="pricing-costing-page">
        {loading && (
          <div className="absolute inset-0 z-30 bg-white/70 flex items-center justify-center rounded-xl">
            <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
          </div>
        )}
        <div className="sticky top-0 z-20 bg-[#FAFAF9] pt-2 pb-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-[#2D2420]">Pricing & Costing</h1>
              <p className="text-sm text-[#A8A8A8]">
                {variant?.master_name || 'Product'} · {variant?.pack_size_label || ''} · {variant?.sku || ''}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={selectorOpen}
                    className="w-72 justify-between border-[#E5E5E5]"
                  >
                    {variant
                      ? `${variant.master_name} · ${variant.pack_size_label}`
                      : 'Select product'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0">
                  <Command>
                    <CommandInput placeholder="Search products..." />
                    <CommandList>
                      <CommandEmpty>No products found.</CommandEmpty>
                      <CommandGroup>
                        {sortedVariants.map((item) => (
                          <CommandItem
                            key={item.id}
                            value={`${item.master_name} ${item.pack_size_label} ${item.sku || ''}`}
                            onSelect={() => {
                              setSelectorOpen(false);
                              navigate(`/products/${item.id}/pricing`);
                            }}
                          >
                            <Check
                              className={variant?.id === item.id ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-0'}
                            />
                            <span className="text-sm text-[#2D2420]">
                              {item.master_name} · {item.pack_size_label}
                              {item.sku ? ` · ${item.sku}` : ''}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {variantId && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/products/${variantId}/pricing-history`)}
                  className="border-[#E5E5E5] px-3"
                >
                  History
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="p-4">
                <p className="text-xs text-[#A8A8A8]">Total Cost / Kg</p>
                <p className="text-lg font-semibold text-[#2D2420]">{formatCurrency(costingTotals.totalCostPerKg)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="p-4">
                <p className="text-xs text-[#A8A8A8]">Total Cost / Pack</p>
                <p className="text-lg font-semibold text-[#2D2420]">{formatCurrency(costingTotals.totalCostPerPack)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-[#A8A8A8]">MSP / Kg</p>
                  <p className="text-lg font-semibold text-[#2D2420]">{formatCurrency(minSelling.minPricePerKg)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#A8A8A8]">Profit</p>
                  <p className="text-sm font-semibold text-[#2D2420]">
                    {formatCurrencyWithDecimals(minSelling.minPricePerKg - costingTotals.totalCostPerKg)}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-[#A8A8A8]">MSP / Pack</p>
                  <p className="text-lg font-semibold text-[#2D2420]">{formatCurrency(minSelling.minPricePerPack)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#A8A8A8]">Profit</p>
                  <p className="text-sm font-semibold text-[#2D2420]">
                    {formatCurrencyWithDecimals(minSelling.minPricePerPack - costingTotals.totalCostPerPack)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: 'End Customer (MRP)', value: pricingForm.retail_mrp },
              { label: 'Retail Shop', value: pricingForm.retail_price },
              { label: 'Wholesale', value: pricingForm.wholesale_price }
            ].map((item) => {
              const price = parseNumber(item.value);
              const status = priceStatus(price);
              const showProfit = true;
              const profit = price - costingTotals.totalCostPerPack;
              const profitColor = profit >= 0 ? 'text-green-600' : 'text-red-600';
              return (
                <Card key={item.label} className="bg-white border-[#E5E5E5]">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-[#A8A8A8]">{item.label} Price</p>
                      <p className={`text-lg font-semibold ${status.color}`}>
                        {status.warn ? '⚠ ' : ''}{formatCurrency(price)}
                      </p>
                    </div>
                    {showProfit ? (
                      <div className="text-right">
                        <p className="text-xs text-[#A8A8A8]">Profit</p>
                        <p className={`text-sm font-semibold ${profitColor}`}>
                          {formatCurrencyWithDecimals(profit)}
                        </p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="space-y-6 max-h-[calc(100vh-360px)] overflow-y-auto pr-2">
          {!variantId && (
            <Card className="bg-white border-[#E5E5E5]">
              <CardContent className="p-6 text-sm text-[#A8A8A8]">
                Select a product to view and update pricing.
              </CardContent>
            </Card>
          )}

          {variantId && (
          <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-[#2D2420]">Costing</h2>
              <p className="text-sm text-[#A8A8A8]">Pack-wise costing inputs with wastage applied on subtotal.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <LabelWithTooltip text="Raw Material Cost / Kg" tooltip="Base raw material cost per kg before processing and packaging." />
                <Input
                  type="number"
                  value={costingForm.raw_material_cost_per_kg}
                  onChange={(e) => setCostingForm({ ...costingForm, raw_material_cost_per_kg: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Processing Cost / Kg" tooltip="Manufacturing or processing cost allocated per kg." />
                <Input
                  type="number"
                  value={costingForm.processing_cost_per_kg}
                  onChange={(e) => setCostingForm({ ...costingForm, processing_cost_per_kg: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Packaging Cost / Kg" tooltip="Packaging materials and packing labor per kg." />
                <Input
                  type="number"
                  value={costingForm.packaging_cost_per_kg}
                  onChange={(e) => setCostingForm({ ...costingForm, packaging_cost_per_kg: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Overhead Cost / Kg" tooltip="Allocated overheads per kg (utilities, admin, rent, etc.)." />
                <Input
                  type="number"
                  value={costingForm.overhead_cost_per_kg}
                  onChange={(e) => setCostingForm({ ...costingForm, overhead_cost_per_kg: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Wastage %" tooltip="Applied on subtotal to cover loss during processing/packing." />
                <Input
                  type="number"
                  value={costingForm.wastage_percentage}
                  onChange={(e) => setCostingForm({ ...costingForm, wastage_percentage: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <LabelWithTooltip text="Change Reason *" tooltip="Required for audit trail. This reason will be stored in history." />
              <Input
                value={costingForm.reason}
                onChange={(e) => setCostingForm({ ...costingForm, reason: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveCosting}
                disabled={saving}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {saving ? 'Saving...' : 'Save Costing'}
              </Button>
            </div>
          </CardContent>
        </Card>
          )}

        {variantId && (
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-[#2D2420]">Non-Negotiable Rules</h2>
              <p className="text-sm text-[#A8A8A8]">MSP & max discount protection by buyer type.</p>
            </div>

            {!rulesReady && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Set costing before saving non-negotiable rules.
              </div>
            )}

            <div className="flex items-center gap-4 text-sm text-[#5C4033]">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rulesForm.no_discount}
                  onChange={(e) => handleToggleNoDiscount(e.target.checked)}
                />
                No Discount
              </label>
              {rulesForm.no_discount && (
                <span className="text-xs text-[#A8A8A8]">All discounts set to flat 0.</span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <LabelWithTooltip text="Minimum Margin %" tooltip="MSP is derived as total cost per pack plus this margin percentage." />
                <Input
                  type="number"
                  value={rulesForm.minimum_margin_percentage}
                  onChange={(e) => setRulesForm({ ...rulesForm, minimum_margin_percentage: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Warning Threshold %" tooltip="Show warning when margin falls below this percentage." />
                <Input
                  type="number"
                  value={rulesForm.warning_threshold_percentage}
                  onChange={(e) => setRulesForm({ ...rulesForm, warning_threshold_percentage: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
              </div>
            </div>

            <Card className="bg-[#FFF7ED] border border-[#F5D0A9]">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm text-[#A15C1B]">Minimum Selling Price (auto)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-[#A15C1B]">Per Kg</p>
                    <p className="text-lg font-semibold text-[#5C4033]">{formatCurrency(minSelling.minPricePerKg)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#A15C1B]">Per Pack</p>
                    <p className="text-lg font-semibold text-[#5C4033]">{formatCurrency(minSelling.minPricePerPack)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <LabelWithTooltip text="Max Discount % (End Customer)" tooltip="Maximum discount allowed on Retail MRP. Effective price shown below." />
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={rulesForm.max_discount_end_customer}
                    onChange={(e) => setRulesForm({ ...rulesForm, max_discount_end_customer: e.target.value })}
                    disabled={rulesForm.no_discount}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                  <Select
                    value={rulesForm.max_discount_end_customer_type}
                    onValueChange={(value) => setRulesForm({ ...rulesForm, max_discount_end_customer_type: value })}
                    disabled={rulesForm.no_discount}
                  >
                    <SelectTrigger className="border-[#E5E5E5] w-36">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percent</SelectItem>
                      <SelectItem value="FLAT">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  const effective = maxDiscountEffectiveByType(
                    pricingForm.retail_mrp,
                    rulesForm.max_discount_end_customer,
                    rulesForm.max_discount_end_customer_type
                  );
                  const status = priceStatus(effective);
                  return (
                    <p className={`text-xs ${status.color}`}>
                      {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                    </p>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Max Discount % (Retail Shop)" tooltip="Maximum discount allowed on Retail shop price. Effective price shown below." />
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={rulesForm.max_discount_retail_shop}
                    onChange={(e) => setRulesForm({ ...rulesForm, max_discount_retail_shop: e.target.value })}
                    disabled={rulesForm.no_discount}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                  <Select
                    value={rulesForm.max_discount_retail_shop_type}
                    onValueChange={(value) => setRulesForm({ ...rulesForm, max_discount_retail_shop_type: value })}
                    disabled={rulesForm.no_discount}
                  >
                    <SelectTrigger className="border-[#E5E5E5] w-36">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percent</SelectItem>
                      <SelectItem value="FLAT">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  const effective = maxDiscountEffectiveByType(
                    pricingForm.retail_price,
                    rulesForm.max_discount_retail_shop,
                    rulesForm.max_discount_retail_shop_type
                  );
                  const status = priceStatus(effective);
                  return (
                    <p className={`text-xs ${status.color}`}>
                      {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                    </p>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Max Discount % (Wholesale)" tooltip="Maximum discount allowed on wholesale price. Effective price shown below." />
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={rulesForm.max_discount_wholesale}
                    onChange={(e) => setRulesForm({ ...rulesForm, max_discount_wholesale: e.target.value })}
                    disabled={rulesForm.no_discount}
                    className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  />
                  <Select
                    value={rulesForm.max_discount_wholesale_type}
                    onValueChange={(value) => setRulesForm({ ...rulesForm, max_discount_wholesale_type: value })}
                    disabled={rulesForm.no_discount}
                  >
                    <SelectTrigger className="border-[#E5E5E5] w-36">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percent</SelectItem>
                      <SelectItem value="FLAT">Flat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  const effective = maxDiscountEffectiveByType(
                    pricingForm.wholesale_price,
                    rulesForm.max_discount_wholesale,
                    rulesForm.max_discount_wholesale_type
                  );
                  const status = priceStatus(effective);
                  return (
                    <p className={`text-xs ${status.color}`}>
                      {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                    </p>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center gap-6 text-sm text-[#5C4033]">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rulesForm.hard_block}
                  onChange={(e) => setRulesForm({ ...rulesForm, hard_block: e.target.checked })}
                />
                <span className="flex items-center gap-2">
                  Hard Block Below MSP
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-[#A8A8A8] hover:text-[#5C4033] transition-colors"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Blocks saving if any price is below MSP.</TooltipContent>
                  </Tooltip>
                </span>
              </label>
              <label className="flex items-center gap-2 text-[#A8A8A8]">
                <input type="checkbox" checked={rulesForm.allow_below_minimum} disabled />
                Allow Below Minimum (disabled)
              </label>
            </div>

            <div className="space-y-2">
              <LabelWithTooltip text="Change Reason *" tooltip="Required for audit trail. This reason will be stored in history." />
              <Input
                value={rulesForm.reason}
                onChange={(e) => setRulesForm({ ...rulesForm, reason: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveRules}
                disabled={saving || !rulesReady}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {saving ? 'Saving...' : 'Save Rules'}
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {variantId && (
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-6 space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-[#2D2420]">Pricing</h2>
              <p className="text-sm text-[#A8A8A8]">Set buyer prices with margin impact.</p>
            </div>

            {!pricingReady && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Set costing and non-negotiable rules before saving pricing.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <LabelWithTooltip text="Retail MRP" tooltip="Price for direct end customers." />
                <Input
                  type="number"
                  value={pricingForm.retail_mrp}
                  onChange={(e) => setPricingForm({ ...pricingForm, retail_mrp: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                {(() => {
                  const effective = parseNumber(pricingForm.retail_mrp);
                  const status = priceStatus(effective);
                  return (
                    <p className={`text-xs ${status.color}`}>
                      {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                    </p>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Retail Shop Price" tooltip="Price for retail shops." />
                <Input
                  type="number"
                  value={pricingForm.retail_price}
                  onChange={(e) => setPricingForm({ ...pricingForm, retail_price: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                {(() => {
                  const effective = parseNumber(pricingForm.retail_price);
                  const status = priceStatus(effective);
                  return (
                    <p className={`text-xs ${status.color}`}>
                      {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                    </p>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Wholesale Price" tooltip="Price for wholesale customers and bulk buyers." />
                <Input
                  type="number"
                  value={pricingForm.wholesale_price}
                  onChange={(e) => setPricingForm({ ...pricingForm, wholesale_price: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                />
                {(() => {
                  const effective = parseNumber(pricingForm.wholesale_price);
                  const status = priceStatus(effective);
                  return (
                    <p className={`text-xs ${status.color}`}>
                      {status.warn ? '⚠ ' : ''}{formatCurrency(effective)}
                    </p>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <LabelWithTooltip text="Pricing Basis" tooltip="Choose whether pricing is cost-based or market-based." />
                <Select
                  value={pricingForm.pricing_basis}
                  onValueChange={(value) => setPricingForm({ ...pricingForm, pricing_basis: value })}
                >
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select basis" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="COST_BASED">Cost Based</SelectItem>
                    <SelectItem value="MARKET_BASED">Market Based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {pricingErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {pricingErrors[0]}
              </div>
            )}

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-[#5C4033]">
                <input
                  type="checkbox"
                  checked={pricingForm.active}
                  onChange={(e) => setPricingForm({ ...pricingForm, active: e.target.checked })}
                />
                Active
              </label>
            </div>

            <div className="space-y-2">
              <LabelWithTooltip text="Change Reason *" tooltip="Required for audit trail. This reason will be stored in history." />
              <Input
                value={pricingForm.reason}
                onChange={(e) => setPricingForm({ ...pricingForm, reason: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleRequestApplyToAllVariants}
                disabled={saving || applyingToAll || pricingErrors.length > 0 || !pricingReady}
                className="border-[#E5E5E5]"
              >
                {applyingToAll ? 'Applying...' : 'Apply to all variants'}
              </Button>
              <Button
                onClick={handleSavePricing}
                disabled={saving || pricingErrors.length > 0 || !pricingReady}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {saving ? 'Saving...' : 'Save Pricing'}
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {variantId && (
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#2D2420]">Discount Slabs</h2>
                <p className="text-sm text-[#A8A8A8]">Quantity based discounts by buyer type.</p>
              </div>
              <Button
                variant="outline"
                onClick={handleAddDiscountSlab}
                disabled={!discountsReady || rulesForm.no_discount}
                className="border-[#E5E5E5]"
              >
                Add Slab
              </Button>
            </div>

            {!discountsReady && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Set pricing and rules before adding discounts.
              </div>
            )}
            {rulesForm.no_discount && (
              <div className="bg-[#FFF7ED] border border-[#F5D0A9] rounded-lg p-3 text-sm text-[#A15C1B]">
                Discounts are disabled because "No Discount" is enabled in non-negotiable rules.
              </div>
            )}

            <div className="space-y-3">
              {discountSlabs.length === 0 ? (
                <p className="text-sm text-[#A8A8A8]">No discount slabs yet.</p>
              ) : (
                discountSlabs.map((slab, index) => (
                  <Card key={index} className="bg-white border-[#E5E5E5]">
                    <CardContent className="p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <LabelWithTooltip text="Buyer Type" tooltip="Select the buyer category this slab applies to." />
                          <Select
                            value={slab.buyer_type}
                            onValueChange={(value) => {
                              const updated = [...discountSlabs];
                              updated[index].buyer_type = value;
                              setDiscountSlabs(updated);
                            }}
                            disabled={rulesForm.no_discount}
                          >
                            <SelectTrigger className="border-[#E5E5E5]">
                              <SelectValue placeholder="Select buyer" />
                            </SelectTrigger>
                            <SelectContent>
                              {buyerTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <LabelWithTooltip text="Discount Type" tooltip="Percentage of base price or flat amount per pack." />
                          <Select
                            value={slab.discount_type}
                            onValueChange={(value) => {
                              const updated = [...discountSlabs];
                              updated[index].discount_type = value;
                              setDiscountSlabs(updated);
                            }}
                            disabled={rulesForm.no_discount}
                          >
                            <SelectTrigger className="border-[#E5E5E5]">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                              <SelectItem value="FLAT">Flat</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <LabelWithTooltip text="Min Quantity (kg)" tooltip="Starting quantity (in kg) for this discount slab." />
                          <Input
                            type="number"
                            value={slab.min_quantity_kg}
                            onChange={(e) => {
                              const updated = [...discountSlabs];
                              updated[index].min_quantity_kg = e.target.value;
                              setDiscountSlabs(updated);
                            }}
                            disabled={rulesForm.no_discount}
                            className="border-[#E5E5E5] focus:border-[#C97A5E]"
                          />
                        </div>
                        <div className="space-y-2">
                          <LabelWithTooltip text="Max Quantity (kg)" tooltip="Ending quantity (in kg) for this discount slab." />
                          <Input
                            type="number"
                            value={slab.max_quantity_kg}
                            onChange={(e) => {
                              const updated = [...discountSlabs];
                              updated[index].max_quantity_kg = e.target.value;
                              setDiscountSlabs(updated);
                            }}
                            disabled={rulesForm.no_discount}
                            className="border-[#E5E5E5] focus:border-[#C97A5E]"
                          />
                        </div>
                        <div className="space-y-2">
                          <LabelWithTooltip text="Discount Value" tooltip="Discount amount. Interpreted as percentage or flat value based on type." />
                          <Input
                            type="number"
                            value={slab.discount_value}
                            onChange={(e) => {
                              const updated = [...discountSlabs];
                              updated[index].discount_value = e.target.value;
                              setDiscountSlabs(updated);
                            }}
                            disabled={rulesForm.no_discount}
                            className="border-[#E5E5E5] focus:border-[#C97A5E]"
                          />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#5C4033]">
                          <input
                            type="checkbox"
                            checked={slab.active}
                            onChange={(e) => {
                              const updated = [...discountSlabs];
                              updated[index].active = e.target.checked;
                              setDiscountSlabs(updated);
                            }}
                            disabled={rulesForm.no_discount}
                          />
                          Active
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          onClick={() => handleRemoveDiscountSlab(index)}
                          disabled={rulesForm.no_discount}
                          className="border-[#E5E5E5]"
                        >
                          Remove
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <div className="space-y-2">
              <LabelWithTooltip text="Change Reason *" tooltip="Required for audit trail. This reason will be stored in history." />
              <Input
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveDiscounts}
                disabled={saving || !discountsReady || rulesForm.no_discount}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {saving ? 'Saving...' : 'Save Discounts'}
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {variantId && (
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#2D2420]">Recent Changes</h2>
                <p className="text-sm text-[#A8A8A8]">Last 10 pricing and costing updates.</p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate(`/products/${variantId}/pricing-history`)}
                className="border-[#E5E5E5]"
              >
                View All
              </Button>
            </div>

            {pricingData.history.length === 0 ? (
              <p className="text-sm text-[#A8A8A8]">No history yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Changed By</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {pricingData.history.slice(0, 10).map((entry) => (
                      <tr key={entry.id}>
                        <td className="py-3 px-4 text-sm text-[#2D2420]">
                          {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.entity_type}</td>
                        <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.changed_by?.name || '-'}</td>
                        <td className="py-3 px-4 text-sm text-[#2D2420]">{entry.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <AlertDialog open={showApplyAllDialog} onOpenChange={setShowApplyAllDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Apply to all variants</AlertDialogTitle>
              <AlertDialogDescription>
                This will update costing, pricing rules, pricing, and discount slabs for all other variants of this product using pack-size scaling based on the current variant.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={applyingToAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-[#C97A5E] hover:bg-[#B06850]"
                onClick={(e) => {
                  e.preventDefault();
                  setShowApplyAllDialog(false);
                  handleApplyToAllVariants();
                }}
                disabled={applyingToAll}
              >
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </div>
      </div>
    </TooltipProvider>
  );
}
